import {
  Component,
  ElementRef,
  input,
  output,
  signal,
  computed,
  viewChild,
  model,
} from '@angular/core';
import { TextFieldModule } from '@angular/cdk/text-field';

@Component({
  imports: [TextFieldModule],
  selector: 'chat-prompt',
  template: `
    <div
      class="search-input"
      [class.search-input--loading]="loading()"
      [class.search-input--disabled]="disabled()"
    >
      <!-- Animated placeholder slot — hidden when field has text or is focused -->
      <div
        class="search-input__ph-slot"
        [class.search-input__ph-slot--hidden]="text().length > 0 || focused()"
        aria-hidden="true"
      >
        <ng-content select="[slot=placeholder]" />
      </div>

      <textarea
        #field
        cdkTextareaAutosize
        #autosize="cdkTextareaAutosize"
        cdkAutosizeMinRows="1"
        class="search-input__field"
        type="text"
        [placeholder]="placeholder()"
        [disabled]="disabled() || loading()"
        [value]="text()"
        (input)="text.set(field.value)"
        (keydown.enter)="onEnterKey($event)"
        (focus)="focused.set(true)"
        (blur)="focused.set(false)"
        aria-label="AI prompt input"
        name="chat-input"
      ></textarea>

      <div class="search-input__actions">
        @if (text().length > 0) {
          <button
            class="search-input__clear-btn"
            type="button"
            title="Clear input"
            aria-label="Clear input"
            (click)="onClear()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        }

        @if (loading()) {
          <button
            class="search-input__send-btn search-input__send-btn--active"
            type="button"
            aria-label="Stop generation"
            (click)="stop.emit()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <rect x="5" y="5" width="14" height="14" rx="2" />
            </svg>
          </button>
        } @else {
          <button
            class="search-input__send-btn"
            [class.search-input__send-btn--active]="canSubmit()"
            type="button"
            aria-label="Send prompt"
            [disabled]="!canSubmit()"
            (click)="onSubmit()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M12 19V5M5 12l7-7 7 7" />
            </svg>
          </button>
        }
      </div>
    </div>
  `,
  styleUrl: './chat-prompt.scss',
})
export class ChatPrompt {
  placeholder = input<string>('Ask me anything');
  disabled = input<boolean>(false);
  loading = input<boolean>(false);

  submit = output<string>();
  stop = output<void>();
  attach = output<FileList>();

  private readonly fieldRef = viewChild.required<ElementRef<HTMLInputElement>>('field');

  text = model.required<string>();
  protected focused = signal(false);
  protected canSubmit = computed(
    () => this.text().trim().length > 0 && !this.disabled() && !this.loading(),
  );

  protected onEnterKey(event: Event): void {
    event.preventDefault();
    if (this.canSubmit()) this.onSubmit();
  }

  protected onSubmit(): void {
    const value = this.text().trim();
    if (!value) return;
    this.submit.emit(value);
    this.text.set('');
  }

  protected onClear(): void {
    this.text.set('');
    this.fieldRef()?.nativeElement.focus();
  }
}
